import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Rating,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import { collection, getDocs, query, orderBy as firebaseOrderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

interface UserStats {
  id: string;
  name: string;
  email: string;
  registrationDate: Date;
  language: string;
  plan: string;
  age?: number;
  country?: string;
  city?: string;
  rating: number | null;
  review: string | null;
  timezone: string;
  dreamsCount: number;
  topCategory: string;
  topCategoryCount: number;
}

interface UserCounts {
  total: number;
  free: number;
  basic: number;
  premium: number;
}

type Order = 'asc' | 'desc';
type OrderBy = keyof UserStats;

const UsersManagement = () => {
  const { currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState<UserStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<OrderBy>('registrationDate');
  const [order, setOrder] = useState<Order>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userCounts, setUserCounts] = useState<UserCounts>({
    total: 0,
    free: 0,
    basic: 0,
    premium: 0
  });

  // Fonction pour obtenir les statistiques des rêves d'un utilisateur
  const getUserDreamStats = async (userId: string) => {
    try {
      const dreamsRef = collection(db, 'users', userId, 'dreams');
      const dreamsSnap = await getDocs(dreamsRef);
      
      const dreamsCount = dreamsSnap.size;
      const categoryCount: { [key: string]: { name: string; count: number } } = {};
      
      // Parcourir tous les rêves pour compter les sous-catégories
      for (const doc of dreamsSnap.docs) {
        const data = doc.data();
        if (data.approaches && data.approaches.length > 0) {
          for (const approach of data.approaches) {
            if (approach.subcategoryName) {
              const name = approach.subcategoryName;
              if (!categoryCount[name]) {
                categoryCount[name] = { name, count: 0 };
              }
              categoryCount[name].count++;
            }
          }
        }
      }

      // Trouver la sous-catégorie la plus utilisée
      let topCategory = 'Aucune';
      let topCount = 0;
      
      Object.values(categoryCount).forEach(({ name, count }) => {
        if (count > topCount) {
          topCategory = name;
          topCount = count;
        }
      });

      return {
        dreamsCount,
        topCategory: topCategory,
        topCategoryCount: topCount
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      return {
        dreamsCount: 0,
        topCategory: 'Erreur',
        topCategoryCount: 0
      };
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      if (!currentUser || !isAdmin) {
        setError("Vous n'avez pas les permissions nécessaires pour voir cette page");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const usersRef = collection(db, 'users');
        const q = query(usersRef, firebaseOrderBy('registrationDate', 'desc'));
        const usersSnap = await getDocs(q);

        let counts: UserCounts = {
          total: 0,
          free: 0,
          basic: 0,
          premium: 0
        };
        
        const usersData = await Promise.all(
          usersSnap.docs.map(async (doc) => {
            const userData = doc.data();
            const dreamStats = await getUserDreamStats(doc.id);
            
            // Incrémenter les compteurs
            counts.total++;
            switch (userData.plan?.toLowerCase() || 'free') {
              case 'basic':
                counts.basic++;
                break;
              case 'premium':
                counts.premium++;
                break;
              default:
                counts.free++;
            }

            return {
              id: doc.id,
              name: userData.displayName || 'Anonyme',
              email: userData.email || '-',
              registrationDate: userData.registrationDate instanceof Date ? userData.registrationDate : new Date(),
              language: userData.language || 'FR',
              plan: userData.plan || 'Free',
              age: userData.age,
              country: userData.country,
              city: userData.city,
              rating: userData.rating || null,
              review: userData.review || null,
              timezone: userData.timezone || '-',
              ...dreamStats
            };
          })
        );

        setUsers(usersData);
        setUserCounts(counts);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des utilisateurs:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [currentUser, isAdmin]);

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortData = (data: UserStats[], orderBy: OrderBy, order: Order) => {
    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === null || bValue === null) {
        return 0;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return order === 'desc' 
          ? bValue.getTime() - aValue.getTime()
          : aValue.getTime() - bValue.getTime();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'desc'
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'desc' ? bValue - aValue : aValue - bValue;
      }

      return 0;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const sortedUsers = sortData([...users], orderBy, order)
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        width: '100%',
        backgroundColor: 'transparent',
        overflow: 'hidden',
        '& .MuiTableCell-root': {
          color: 'white',
          borderColor: 'rgba(104, 241, 209, 0.2)'
        },
        '& .MuiTableCell-head': {
          backgroundColor: 'rgba(26, 27, 53, 0.8)',
          color: '#68f1d1',
          fontWeight: 'bold'
        },
        '& .MuiTableSortLabel-root': {
          color: '#68f1d1',
          '&.Mui-active': {
            color: '#68f1d1',
          },
          '&:hover': {
            color: '#5fdfb4',
          },
        },
        '& .MuiTableSortLabel-icon': {
          color: '#68f1d1 !important',
        },
        '& .MuiTablePagination-root': {
          color: 'white',
        },
        '& .MuiTablePagination-select': {
          color: 'white',
        },
        '& .MuiTablePagination-selectIcon': {
          color: '#68f1d1',
        },
      }}
    >
      {/* Statistiques */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr 1fr',
            sm: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        {[
          { label: 'Total Utilisateurs', value: userCounts.total },
          { label: 'Plan Free', value: userCounts.free },
          { label: 'Plan Basic', value: userCounts.basic },
          { label: 'Plan Premium', value: userCounts.premium },
        ].map((stat, index) => (
          <Paper
            key={index}
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              backgroundColor: 'rgba(26, 27, 53, 0.8)',
              border: '1px solid rgba(104, 241, 209, 0.2)',
              borderRadius: '12px',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: '#68f1d1',
                fontWeight: 600,
                mb: 1,
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                opacity: 0.7,
              }}
            >
              {stat.label}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Tableau */}
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Nom
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'registrationDate'}
                  direction={orderBy === 'registrationDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('registrationDate')}
                >
                  Date d'inscription
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'language'}
                  direction={orderBy === 'language' ? order : 'asc'}
                  onClick={() => handleRequestSort('language')}
                >
                  Langue
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'plan'}
                  direction={orderBy === 'plan' ? order : 'asc'}
                  onClick={() => handleRequestSort('plan')}
                >
                  Plan
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'age'}
                  direction={orderBy === 'age' ? order : 'asc'}
                  onClick={() => handleRequestSort('age')}
                >
                  Âge
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'country'}
                  direction={orderBy === 'country' ? order : 'asc'}
                  onClick={() => handleRequestSort('country')}
                >
                  Pays
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'city'}
                  direction={orderBy === 'city' ? order : 'asc'}
                  onClick={() => handleRequestSort('city')}
                >
                  Ville
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'rating'}
                  direction={orderBy === 'rating' ? order : 'asc'}
                  onClick={() => handleRequestSort('rating')}
                >
                  Note
                </TableSortLabel>
              </TableCell>
              <TableCell>Avis</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'timezone'}
                  direction={orderBy === 'timezone' ? order : 'asc'}
                  onClick={() => handleRequestSort('timezone')}
                >
                  Fuseau horaire
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'dreamsCount'}
                  direction={orderBy === 'dreamsCount' ? order : 'asc'}
                  onClick={() => handleRequestSort('dreamsCount')}
                >
                  Nombre de rêves
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'topCategory'}
                  direction={orderBy === 'topCategory' ? order : 'asc'}
                  onClick={() => handleRequestSort('topCategory')}
                >
                  Catégorie favorite
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow 
                key={user.id}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(104, 241, 209, 0.1)',
                  }
                }}
              >
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.registrationDate.toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </TableCell>
                <TableCell>{user.language}</TableCell>
                <TableCell>{user.plan}</TableCell>
                <TableCell>{typeof user.age === 'number' ? user.age : '-'}</TableCell>
                <TableCell>{user.country || '-'}</TableCell>
                <TableCell>{user.city || '-'}</TableCell>
                <TableCell>
                  <Rating 
                    value={user.rating} 
                    readOnly 
                    size="small"
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: 'var(--star-icons)',
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  {user.review ? (
                    <Tooltip title={user.review} arrow placement="top">
                      <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                        {user.review.substring(0, 50)}
                        {user.review.length > 50 ? '...' : ''}
                      </Typography>
                    </Tooltip>
                  ) : '-'}
                </TableCell>
                <TableCell>{user.timezone}</TableCell>
                <TableCell>{user.dreamsCount}</TableCell>
                <TableCell>
                  <Tooltip 
                    title={`Utilisée ${user.topCategoryCount} fois`}
                    arrow 
                    placement="top"
                  >
                    <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                      {user.topCategory} ({user.topCategoryCount})
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Lignes par page"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} sur ${count}`
        }
      />
    </Paper>
  );
};

export default UsersManagement;
